import { DeleteOutlineRounded, EditOutlined, GetAppOutlined, VisibilityOutlined } from '@mui/icons-material'
import { Box, styled, Typography, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import GenericTable from 'src/components/table/GenericTable'
import AccordionCommon from 'src/components/AccordionCommon'
import { FormSchemaAccordionHeader, getStatusConfig } from 'src/components/report-templates/FormSchemaAccordionHeader'
import { IFormSchemaReportTemplates, IReportTemplate } from 'src/generated-types/report-template'
//The location filter is hidden for this version. Will be available once the locations are clarified
//import { ReportTemplateLocation, ReportTemplateStatus } from 'src/enums'
import { ReportTemplateStatus } from 'src/enums'
import { IDropdownDto } from 'src/generated-types/api-result'
import { Spinner } from '../Spinner'
import { useDialogCtx } from 'src/hooks/context-hooks'
import GenerateReportModal, { ITemplateOption } from './GenerateReportModal'
import { GridActionButton } from '../common/GridActionButton'

export interface IFormSchemaAccordionItemProps {
  schemaData: IFormSchemaReportTemplates
  expanded: boolean
  onExpandChange: (expanded: boolean) => void
  onCreateTemplate: (formSchemaId: number) => void
  onViewTemplate: (template: IReportTemplate) => void
  onEditTemplate: (formSchema: IDropdownDto, initialData?: IReportTemplate) => void
  onDeleteTemplate: (template: IReportTemplate) => void
  customerId?: EntityId
  isPreviewLoading?: boolean
}

export const FormSchemaAccordionItem: React.FC<IFormSchemaAccordionItemProps> = ({
  schemaData,
  expanded,
  onExpandChange,
  onCreateTemplate,
  onViewTemplate,
  onEditTemplate,
  onDeleteTemplate,
  customerId,
  isPreviewLoading = false,
}) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['dynamic-report', 'common'])
  const STATUS_CONFIG = useMemo(() => getStatusConfig(theme), [theme])
  const [generatingTemplateId, setGeneratingTemplateId] = React.useState<number | null>(null)
  const [previewingTemplateId, setPreviewingTemplateId] = React.useState<number | null>(null)
  const { showDialog } = useDialogCtx()

  React.useEffect(() => {
    if (!isPreviewLoading) {
      setPreviewingTemplateId(null)
    }
  }, [isPreviewLoading])

  const formSchema = { id: schemaData.formSchemaId, name: schemaData.formSchemaName } as IDropdownDto

  const handleGenerateTemplate = (templateId: number, templateName: string) => {
    showDialog(
      GenerateReportModal,
      {
        componentProps: {
          customerId,
          reportTemplateId: templateId,
          openedFromTemplateRow: true,
          setGeneratingTemplateId,
          selectedTemplate: { id: templateId, name: templateName } as ITemplateOption,
          formSchemaId: schemaData.formSchemaId,
        },
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: translate('template-name', { ns: 'dynamic-report' }),
        flex: 30,
        renderCell: params => (
          <Box>
            <Typography variant="body1" fontWeight={500} lineHeight={1.3}>
              {params.row.name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: translate('status', { ns: 'dynamic-report' }),
        flex: 42, // change flex to 18 once location column available
        renderCell: params => {
          const statusConfig = STATUS_CONFIG[params.row.status as ReportTemplateStatus]
          return statusConfig ? (
            <StatusChip bgColor={statusConfig.bgColor} chipColor={statusConfig.color}>
              {translate(statusConfig.labelKey, { ns: 'dynamic-report' }).toUpperCase()}
            </StatusChip>
          ) : null
        },
      },
      //The location filter is hidden for this version. Will be available once the locations are clarified
      // {
      //   field: 'locations',
      //   headerName: translate('locations', { ns: 'dynamic-report' }),
      //   flex: 35,
      //   renderCell: params => (
      //     <Box display="flex" flexWrap="wrap" gap={0.5}>
      //       {(params.row.locations as ReportTemplateLocation[]).map(loc => (
      //         <LocationChip key={loc}>
      //           {translate(ReportTemplateLocation[loc] as string, { ns: 'dynamic-report' })}
      //         </LocationChip>
      //       ))}
      //     </Box>
      //   ),
      // },

      {
        field: 'actions',
        headerName: translate('actions', { ns: 'common' }),
        flex: 9, // change flex to 11 once location column available
        align: 'left',
        headerAlign: 'left',
        renderCell: params => (
          <Box display="flex" justifyContent="flex-start" gap={0.5} width="100%">
            <GridActionButton
              disabled={
                generatingTemplateId === params.row.id ||
                params.row.status === ReportTemplateStatus.Inactive ||
                params.row.status === ReportTemplateStatus.Draft
              }
              height="32px"
              width="32px"
              onClick={() => handleGenerateTemplate(params.row.id as number, params.row.name as string)}
              buttonIcon={
                generatingTemplateId === params.row.id ? (
                  <Spinner size={18} />
                ) : (
                  <GetAppOutlined sx={{ fontSize: '24px' }} />
                )
              }
              toolTipTitle={translate('action-generate', { ns: 'dynamic-report' }) as string}
            />
            <GridActionButton
              size="small"
              onClick={() => {
                setPreviewingTemplateId(params.row.id as number)
                onViewTemplate(params.row as IReportTemplate)
              }}
              disabled={
                params.row.status === ReportTemplateStatus.Inactive ||
                (isPreviewLoading && previewingTemplateId === params.row.id)
              }
              buttonIcon={
                isPreviewLoading && previewingTemplateId === params.row.id ? (
                  <Spinner size={18} />
                ) : (
                  <VisibilityOutlined sx={{ fontSize: '24px' }} />
                )
              }
              height="32px"
              width="32px"
              toolTipTitle={translate('action-view', { ns: 'dynamic-report' }) as string}
            />

            <GridActionButton
              size="small"
              onClick={() => onEditTemplate(formSchema, params.row as IReportTemplate)}
              buttonIcon={<EditOutlined sx={{ fontSize: '24px' }} />}
              height="32px"
              width="32px"
              toolTipTitle={translate('action-edit', { ns: 'dynamic-report' }) as string}
            />
            <GridActionButton
              size="small"
              onClick={() => onDeleteTemplate(params.row as IReportTemplate)}
              buttonIcon={<DeleteOutlineRounded sx={{ fontSize: '24px' }} />}
              height="32px"
              width="32px"
              toolTipTitle={translate('action-delete', { ns: 'dynamic-report' }) as string}
            />
          </Box>
        ),
      },
    ],
    [
      translate,
      handleGenerateTemplate,
      onViewTemplate,
      onEditTemplate,
      onDeleteTemplate,
      STATUS_CONFIG,
      isPreviewLoading,
      generatingTemplateId,
      previewingTemplateId,
    ],
  )

  return (
    <AccordionCommon
      id={schemaData.formSchemaId}
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandChange(isExpanded)}
      removeBoxShadow={false}
      borderRadius="8px"
      expandedBottomMargin="8px"
      expandedTopMargin="0px"
      accordionHeight="50px"
      title={
        <FormSchemaAccordionHeader
          formSchemaId={schemaData.formSchemaId}
          formSchemaName={schemaData.formSchemaName}
          reportTemplates={schemaData.reportTemplates}
          onCreateTemplate={() => onCreateTemplate(schemaData.formSchemaId)}
          isFormSchemaDeleted={schemaData.isFormSchemaDeleted}
          isFormSchemaInactive={schemaData.isFormSchemaInactive}
        />
      }
    >
      <GenericTable
        id={`report-templates-${schemaData.formSchemaId}`}
        columns={columns}
        data={schemaData.reportTemplates}
        getRowId={row => row.id ?? `${row.name}-${row.status}`}
        autoHeight
        enablePagination={false}
        noRowsMessage={translate('no-templates')}
        getRowHeight={() => 44}
      />
    </AccordionCommon>
  )
}

//The location filter is hidden for this version. Will be available once the locations are clarified
// const LocationChip = styled(Box)(({ theme }) => ({
//   height: '22px',
//   fontSize: '14px',
//   fontWeight: 500,
//   border: `1px solid ${theme.palette.grey[300]}`,
//   backgroundColor: theme.palette.grey[100],
//   '& .MuiChip-label': { padding: '0 8px' },
//   display: 'inline-flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   padding: '4px 10px',
//   borderRadius: '4px',
// }))

const StatusChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'bgColor' && prop !== 'chipColor',
})<{ bgColor: string; chipColor: string }>(({ bgColor, chipColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 10px',
  borderRadius: '4px',
  backgroundColor: bgColor,
  color: chipColor,
  fontWeight: 500,
  fontSize: '12px',
  letterSpacing: '0.05em',
  width: '100px',
}))
